import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { usersApi } from '@/shared/api/users';
import { isAuthlessPath, useAuthStore } from '@/features/auth';
import { findAnchor, resolveSteps, tourFor, tourText, useTourStore, type ResolvedStep, type Tour } from '@/features/onboarding-tour';
import { MenuTourRun } from './MenuTourRun';
import { TourCard } from './TourCard';
import { useSpotlight } from './use-spotlight';
import { useTourContext } from './use-tour-context';

const MENU_QUERY = '(max-width: 1100px)';
const AUTO_START_DELAY_MS = 1200;

export function OnboardingTour() {
  const tour = useTourStore((s) => s.tour);
  const replay = useTourStore((s) => s.replay);
  const narrow = useMediaQuery(MENU_QUERY);
  useAutoStart();

  const finish = useCallback(() => {
    if (!tour) return;
    useTourStore.getState().stop();
    if (replay) return;
    useAuthStore.getState().setTourSeen(tour.id, tour.version);
    usersApi.markTourSeen(tour.id, tour.version).catch(() => undefined);
  }, [tour, replay]);

  if (!tour) return null;
  const key = `${tour.id}:${replay}:${narrow}`;
  return narrow ? (
    <MenuTourRun key={key} tour={tour} onFinish={finish} />
  ) : (
    <TourRun key={key} tour={tour} onFinish={finish} />
  );
}

function useAutoStart() {
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const running = useTourStore((s) => s.tour != null);
  const { pathname } = useLocation();
  const toursSeen = user?.toursSeen;
  const tour = toursSeen ? tourFor(hasPermission) : null;
  const due = tour != null && (toursSeen?.[tour.id] ?? 0) < tour.version;
  const blockedPath = pathname === '/login' || isAuthlessPath(pathname);

  useEffect(() => {
    if (!due || running || blockedPath || !tour) return;
    const timer = window.setTimeout(() => {
      if (document.documentElement.hasAttribute('data-dialog-open')) return;
      useTourStore.getState().start(tour, false);
    }, AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [due, running, blockedPath, tour]);
}

function TourRun({ tour, onFinish }: { tour: Tour; onFinish: () => void }) {
  const ctx = useTourContext();
  const [steps] = useState<ResolvedStep[]>(() => resolveSteps(tour.steps, ctx, (a) => findAnchor(a) != null));
  const [index, setIndex] = useState(0);
  const popupRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const step = steps[index] as ResolvedStep | undefined;
  const last = index === steps.length - 1;
  const anchor = step?.spot ? step.anchor : null;

  useEffect(() => {
    useTourStore.getState().setAnchor(anchor);
  }, [anchor]);

  useSpotlight(anchor, { popupRef, side: step?.side ?? 'bottom', sheet: false, scroll: 'nearest' });

  if (!step) return null;

  return (
    <TourCard
      popupRef={popupRef}
      nextRef={nextRef}
      modal
      sheet={false}
      anchored={step.spot}
      pass={false}
      counter={`Шаг ${index + 1} из ${steps.length}`}
      title={step.title}
      body={tourText(step.body, ctx)}
      nextLabel={last ? 'Готово' : 'Далее'}
      onNext={() => (last ? onFinish() : setIndex((i) => i + 1))}
      onBack={index > 0 ? () => setIndex((i) => Math.max(0, i - 1)) : undefined}
      onSkip={last ? undefined : onFinish}
      onClose={onFinish}
    />
  );
}
